import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image/Image'
import Link from 'UIKit/Link/Link'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text/Text'
import { RouteConfig } from 'config/constants/route'
import { UserBonus } from 'config/types/bonus/userBonus'
import { Game } from 'config/types/game'
import { useAnalytics } from 'hooks/useAnalytics'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import { useRouter } from 'hooks/useRouter'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import React, { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useActiveBonusInWageringProcess, useActiveBoosterWagerBonus } from 'state/bonus/hooks'
import { useFeaturedGame } from 'state/profile/hooks'
import { timeLeftUnit } from 'utils'
import { getFullDisplayBalance } from 'utils/formatBalance'
import BonusItem from './BonusItem'
import {
  ModalWrapper,
  ProgressBar,
  ScaleVariantBonus,
  StyledArrowButton,
  StyledButton,
  StyledTitle,
  Wrapper,
  WrapperContentDropdown,
} from './styled'

const BonusWagerDropdown: React.FC<{ bonus: UserBonus }> = ({ bonus }) => {
  return (
    <WrapperContentDropdown p="0 !important" mt="6px !important">
      <BonusItem bonus={bonus} />
    </WrapperContentDropdown>
  )
}
const BonusWagerModal: React.FC<ModalProps & { bonus: UserBonus }> = ({ bonus, onDismiss, ...props }) => {
  return (
    <ModalWrapper title="Bonus Wager" onDismiss={onDismiss} {...props}>
      <BonusItem bonus={bonus} onDismiss={onDismiss} />
    </ModalWrapper>
  )
}

const BonusWager: React.FC<{ game: Game } & ScaleVariantBonus & BoxProps> = ({ game, tagButtonEnable, ...props }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { recordEvent } = useAnalytics()
  const bonusWagering = useActiveBonusInWageringProcess()
  const boosterWagerBonusActive = useActiveBoosterWagerBonus()
  const isClaimToPlayBonusWagering = bonusWagering?.isCreaditReward
  const isApplyBoosterWagerBonus = !isClaimToPlayBonusWagering && boosterWagerBonusActive

  const { isDesktop } = useMatchBreakpoints()

  const seconds = Math.floor(((bonusWagering?.condition?.expiryDate || 0) - new Date().getTime()) / 1000)
  const time = timeLeftUnit(bonusWagering?.condition?.expiryDate)
  const [onPresent] = useModal((props) => <BonusWagerModal bonus={bonusWagering} {...props} />)
  const {
    featuredGameInfo: { isWageringBonusGame },
  } = useFeaturedGame(game.code)

  useEffect(() => {
    recordEvent('play_game', {
      gameSlug: game.slug,
      isWagering: isWageringBonusGame,
    })
  }, [game])

  return (
    <Wrapper
      dropdownContent={bonusWagering && isDesktop ? <BonusWagerDropdown bonus={bonusWagering} /> : <></>}
      dropdownPosition="bottom-left"
      title={
        <RowBetween width="100%">
          <StyledTitle fontSize={['12px !important', '', '10px !important']}>
            <Trans>Bonus Wager</Trans>
          </StyledTitle>
          {seconds > 0 && (
            <StyledTitle
              fontSize={['12px !important', '', '10px !important']}
              fontWeight={['bold !important']}
              ml="8px"
            >
              {t('{{days}} left', {
                days: time,
              })}
            </StyledTitle>
          )}
        </RowBetween>
      }
      onClick={() => {
        if (bonusWagering && !isDesktop) {
          onPresent()
        }
      }}
      style={{
        cursor: 'auto',
      }}
      {...props}
    >
      {bonusWagering ? (
        isWageringBonusGame ? (
          <RowBetween>
            <ProgressBar progress={bonusWagering.progress}>
              <RowMiddle>
                <Box mr="6px" ml="4px" minWidth="28px">
                  <Image src={bonusWagering.info.image} width={24} height={24} />
                </Box>

                <RowBetween>
                  <Text
                    maxWidth={['100%', '', '80%', '', '', '100%']}
                    mr="8px"
                    style={{
                      wordBreak: 'break-all',
                    }}
                    ellipsis={1}
                    fontSize={['14px', '', '12px']}
                    bold
                  >
                    {getFullDisplayBalance(bonusWagering.bonusAmount.amount, 0, 5)}{' '}
                    {bonusWagering.bonusAmount.token.name}
                  </Text>

                  <Text fontSize={['14px', '', '12px']} bold>
                    {bonusWagering.progress}%
                  </Text>
                </RowBetween>
              </RowMiddle>
            </ProgressBar>
            {isApplyBoosterWagerBonus && (
              <Box ml="8px" minWidth="16px">
                <Image src="/images/bonusCenter/boost-wager-icon.png" width={16} height={16} />
              </Box>
            )}
            <StyledArrowButton ml="8px" disabled />
          </RowBetween>
        ) : (
          <RowBetween height="100%">
            <RowMiddle>
              <Box width="24px">
                <Image src={bonusWagering?.info.image} width={24} height={24} />
              </Box>
              <Text
                maxWidth={['100%', '', '80%', '', '', '100%']}
                style={{
                  wordBreak: 'break-all',
                }}
                bold
                ellipsis={1}
                fontSize={['14px', '', '12px']}
                ml="4px"
              >
                <Trans>Not applied</Trans>
              </Text>
            </RowMiddle>

            <StyledButton
              height="24px !important"
              ml="8px"
              onClick={() => {
                router.push({
                  pathname: RouteConfig.Games,
                  query: { gameCollection: bonusWagering.gameCollectionId },
                })
              }}
              display={tagButtonEnable ? 'flex !important' : 'none !important'}
            >
              <Trans>Other Games</Trans>
            </StyledButton>
          </RowBetween>
        )
      ) : (
        <RowBetween height="100%">
          <RowMiddle height="100%">
            <Text
              style={{
                whiteSpace: 'nowrap',
              }}
              fontSize={['14px', '', '12px']}
              bold
            >
              <Trans>No Bonus</Trans>
            </Text>
          </RowMiddle>
          <StyledButton
            height="24px !important"
            ml="8px"
            as={Link}
            href={RouteConfig.BonusCenter}
            display={tagButtonEnable ? 'flex !important' : 'none !important'}
          >
            <Trans>Bonus Center</Trans>
          </StyledButton>
        </RowBetween>
      )}
    </Wrapper>
  )
}

export default BonusWager
